import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Heart, MessageSquare, Users } from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import { BlogFooter } from "../blog/BlogPages";
import { api, isGiggleNull } from "../rsvp/api";
import { formatTimeAgo } from "../rsvp/format";
import "../blog/blog.css";
import "../styles/EventsPage.css";
import "../styles/Communities.css";

const APP_LINK = "https://apps.apple.com/us/app/giggle-make-friends-chat/id6498945325";

/** /communities/:id: a public community and its posts (read-only; replies live in the app). */
const CommunityPage = () => {
  const { communityId } = useParams();
  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data } = await api.publicCommunityFeed(Number(communityId), p);
      setCommunity(data.community);
      setPosts((prev) => (p === 1 ? data.posts || [] : [...prev, ...(data.posts || [])]));
      setHasMore(!!data.has_more);
      setPage(p);
      document.title = `${data.community?.name || "Community"} · Blox`;
    } catch (e) {
      setError(e.status === 404 ? "This community isn't public." : "We couldn't load this community. Please refresh in a moment.");
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => { window.scrollTo(0, 0); load(1); }, [load]);

  return (
    <main className="blog-page">
      <SiteHeader />
      {error ? (
        <section className="community-wrap"><p className="events-empty">{error}</p></section>
      ) : !community ? (
        <section className="community-wrap"><div className="community-hero community-hero-loading" /></section>
      ) : (
        <>
          {/* Full-width cover with the name over it */}
          <section className="community-hero" style={community.cover ? { backgroundImage: `url("${encodeURI(community.cover)}")` } : undefined}>
            <div className="community-hero-shade" />
            <div className="community-hero-inner">
              <Link to="/communities" className="community-back"><ArrowLeft size={16} /> All communities</Link>
              <h1>{community.name}</h1>
              <div className="community-hero-stats">
                <span><Users size={16} /> {community.members} {community.members === 1 ? "member" : "members"}</span>
                <span><MessageSquare size={16} /> {posts.length}{hasMore ? "+" : ""} {posts.length === 1 ? "post" : "posts"}</span>
              </div>
            </div>
          </section>

          <section className="community-layout">
            {/* About panel (sticky on wide screens) */}
            <aside className="community-about">
              <p className="community-label">About</p>
              <p className="community-about-text">{community.description || "A Blox community."}</p>
              <div className="community-about-stats">
                <div><strong>{community.members}</strong><span>{community.members === 1 ? "member" : "members"}</span></div>
                <div><strong>{posts.length}{hasMore ? "+" : ""}</strong><span>{posts.length === 1 ? "post" : "posts"}</span></div>
              </div>
              <a className="community-join" href={APP_LINK} target="_blank" rel="noreferrer">Join on the Blox app</a>
              <p className="community-about-note">Posting, replying and voting happen in the app.</p>
            </aside>

            {/* Posts fill the rest of the width */}
            <div className="community-main">
              {posts.length === 0 && !loading ? (
                <p className="events-empty community-empty">No posts yet. Join the community in the Blox app and start the conversation.</p>
              ) : (
                <div className="community-grid">
                  {posts.map((p) => {
                    const pic = p.author?.picture && !isGiggleNull(p.author.picture) ? p.author.picture : null;
                    return (
                      <article className="community-post" key={p.id}>
                        <div className="community-post-author">
                          {pic ? <img src={pic} alt="" /> : <span className="community-avatar">{(p.author?.name || "?").charAt(0).toUpperCase()}</span>}
                          <div>
                            <strong>{p.author?.name || "Anonymous"}</strong>
                            <span>{p.author?.username ? `@${p.author.username} · ` : ""}{formatTimeAgo(p.created_at)}</span>
                          </div>
                        </div>
                        {p.title && <h3>{p.title}</h3>}
                        {p.text && <p className="community-post-text">{p.text}</p>}
                        {p.poll_options?.length > 0 && (
                          <ul className="community-poll">{p.poll_options.map((o, i) => <li key={i}>{o}</li>)}</ul>
                        )}
                        {p.images?.length > 0 && (
                          <div className={`community-images community-images-${Math.min(p.images.length, 3)}`}>
                            {p.images.slice(0, 3).map((src) => <img key={src} src={src} alt="" loading="lazy" />)}
                          </div>
                        )}
                        <div className="community-post-stats">
                          <span><Heart size={14} /> {p.votes}</span>
                          <span><MessageSquare size={14} /> {p.replies} {p.replies === 1 ? "reply" : "replies"}</span>
                        </div>
                      </article>
                    );
                  })}
                  <a className="community-cta" href={APP_LINK} target="_blank" rel="noreferrer">
                    <strong>Got something to say?</strong>
                    <span>Post, vote in polls and reply to people in {community.name} on the Blox app.</span>
                    <em>Open the Blox app →</em>
                  </a>
                </div>
              )}
              {hasMore && (
                <button type="button" className="community-more" onClick={() => load(page + 1)} disabled={loading}>
                  {loading ? "Loading…" : "Load more posts"}
                </button>
              )}
            </div>
          </section>
        </>
      )}
      <BlogFooter />
    </main>
  );
};

export default CommunityPage;
